import React, {useEffect} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {Skeleton} from '@mui/material';
import {styled} from './styled';
import {composeClasses} from './utils/composeClasses';
import {useTheme} from './hooks';
import type {WithClasses} from './types';
import useDispatcher from '@netx/core-components/hooks/useDispatcher';
import useImageLoader from '@netx/core-components/hooks/useImageLoader';
import {URL} from '@netx/core-next/url';

import {
	logoClasses,
	getLogoUtilityClass,
	LogoClasses,
	LOGO_CLASS_NAME,
} from './logoClasses';
import {useConstCallback} from '../hooks/useEvent';

type LogoOwnerState = WithClasses<LogoClasses, {}>;

const useUtilityClasses = (ownerState: LogoOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		image: ['image'],
		skeleton: ['skeleton'],
	};
	return composeClasses(slots, getLogoUtilityClass, classes);
};

const LogoRoot = styled('div', {
	name: LOGO_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
	maxHeight: 42,
}));

const LogoImage = styled('img', {
	name: LOGO_CLASS_NAME,
	slot: 'Image',
})(({theme}) => ({
	maxHeight: 42,
	maxWidth: '100%',
	width: 'auto',
	height: 'auto',
	[theme.breakpoints.down('sm')]: {
		maxHeight: 32,
	},
}));

const LogoSkeleton = styled(Skeleton, {
	name: LOGO_CLASS_NAME,
	slot: 'Skeleton',
})(({theme}) => ({
	borderRadius: 4,
	width: 116,
	height: 42,
	[theme.breakpoints.down('sm')]: {
		height: 32,
		width: 88,
	},
}));

export type LogoProps = WithClasses<LogoClasses, {}>;

export const LogoPrimitive = ({classes: _classes, className}: LogoProps) => {
	const theme = useTheme();
	const imageLoader = useImageLoader();

	const classes = useUtilityClasses({
		classes: _classes,
		className,
	});

	const handleLogoUpdate = useConstCallback(() => {
		const logoUrl =
			URL.themeLogo ||
			(theme.palette.mode === 'dark' ? URL.logoDark : URL.logoLight);
		imageLoader.load(`${logoUrl}?cb=${Date.now()}`);
	});

	useEffect(() => {
		handleLogoUpdate();
	}, [theme.palette.mode]);

	useDispatcher('app:system:branding:updated', handleLogoUpdate);
	useDispatcher('app:system:branding:reset:confirmed', handleLogoUpdate);

	return (
		<LogoRoot className={clsx(classes.root, className)}>
			{imageLoader.src ? (
				<LogoImage
					className={classes.image}
					src={imageLoader.src}
					alt={i18n.t('i18n.logo')}
				/>
			) : (
				<LogoSkeleton className={classes.skeleton} variant="rectangular" />
			)}
		</LogoRoot>
	);
};

export const Logo = LogoPrimitive;

export default Logo;
